import { useEffect, useState } from "react";
import {
  useCreateBackOfficeUserMutation,
  useDeleteBackOfficeUserMutation,
  useGetAllUserMutation,
  useDeleteManyBackOfficeUserMutation,
  useUpdateOrganisationUserMutation,
  useInviteManyBackOfficeUserMutation,
} from "../../features/user/userApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import toast from "react-hot-toast";
import { useFetchOrganisationRolesMutation } from "../../features/organisation/organisationApiSlice";
import { useNavigate } from "react-router-dom";
import { isSystemAdmin } from "../../utilities/roleChecks";
import FilterSelect from "../../components/Filter/FilterSelect";
import Loading from "../../components/Loading";
import { ACTIVITY_STATUS, ACTIVITY_TYPE } from "../../constants/config.constants";
import { useCreateLogsMutation } from "../../features/logs/logsApiSlice";

const UserStatus = {
  JOINED: "Active",
  INVITED: "Invite Sent",
  REMOVED: "Inactive",
};

const BackOfficeRole = {
  1: "Admin",
  2: "Scheduler",
  3: "Executive",
  4: "Creator",
};

const RoleToId = {
  Admin: 1,
  Scheduler: 2,
  Executive: 3,
  Creator: 4,
};

const UserManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [organisationRoles, setOrganisationRoles] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const currentUser = useSelector(selectUser);
  const navigate = useNavigate();

  // Check if user is System Admin, redirect if not
  useEffect(() => {
    if (currentUser && !isSystemAdmin(currentUser)) {
      navigate("/home", { replace: true });
    }
  }, [currentUser, navigate]);

  const [users, setUsers] = useState([]);

  const [getAllUserAction, { isLoading: isGetLoading }] =
    useGetAllUserMutation();
  const [createBackOfficeUserAction, { isLoading: isCreateLoading }] =
    useCreateBackOfficeUserMutation();
  const [deleteUserAction, { isLoading: isDeleteLoading }] =
    useDeleteBackOfficeUserMutation();
  const [updateUserAction, { isLoading: isUpdateLoading }] =
    useUpdateOrganisationUserMutation();
  const [deleteManyBackOfficeUserAction, { isLoading: isDeleteManyLoading }] =
    useDeleteManyBackOfficeUserMutation();
  const [inviteManyBackOfficeUserAction, { isLoading: isInviteManyLoading }] =
    useInviteManyBackOfficeUserMutation();
  const [fetchOrganisationRolesAction, { isLoading: isFetchOrganisationRolesLoading }] =
    useFetchOrganisationRolesMutation();

    const [createLog] = useCreateLogsMutation();

  const loading = isGetLoading || isCreateLoading || isDeleteLoading || isUpdateLoading || isDeleteManyLoading || isInviteManyLoading || isFetchOrganisationRolesLoading || isSubmitting;

  const fetchUsers = async () => {
    try {
      const { data } = await getAllUserAction().unwrap();

      setUsers(data);
    } catch (error) {
      console.error(error.data.message);
    }
  };

  const deleteUser = async (selectedUser) => {
    const payload = {
      activity: `User ${selectedUser.firstname} ${selectedUser.lastname} has been ${selectedUser.status === "REMOVED" ? "reactivated" : "deleted"} by User - ${currentUser?.details?.name || ""
        }`,
      type: ACTIVITY_TYPE.USER_DELETE,
      data: {
        userId: selectedUser.id,
        type: selectedUser.type,
      },
    };
    try {
      setIsSubmitting(true);
      await deleteUserAction({
        userId: selectedUser.id,
        type: selectedUser.type,
      }).unwrap();

      const usersList = users.map((user) => {
        return user.id === selectedUser.id
          ? {
            ...user,
            status: selectedUser.status === "REMOVED" ? "JOINED" : "REMOVED",
            roles: selectedUser.status === "JOINED" ? [] : selectedUser.roles,
          }
          : user;
      });

      setUsers(usersList);
      toast.success("User " + (selectedUser.status === "REMOVED" ? "reactivated" : "deleted") + " successfully");
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.SUCCESS }).unwrap();
    } catch (error) {
      toast.error(error.data?.message || "Failed to " + (selectedUser.status === "REMOVED" ? "reactivate" : "delete") + " user");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.FAILED }).unwrap();
    }
  };

  const createUser = async () => {
    const userData = {
      userData: {
        type: 1,
        phone: phoneNumber || null,
        email: email,
      },
      staffData: {
        organisation_id: currentUser.organisation.id,
        firstname: firstName,
        lastname: lastName,
        role: Number(role),
      },
    };
    const payload = {
      activity: `User ${firstName} ${lastName} has been added by User - ${currentUser?.details?.name || ""
        }`,
      type: ACTIVITY_TYPE.USER_ADD,
      data: userData,
    };
    try {
      setIsSubmitting(true);

      const { data } = await createBackOfficeUserAction(userData).unwrap();

      setUsers([data, ...users]);
      toast.success("User added successfully");
      setIsModalOpen(false);
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.SUCCESS }).unwrap();
      return true;
    } catch (error) {
      toast.error(error.data?.message || "Failed to send invitation");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.FAILED }).unwrap();
      return false;
    }
  };

  const updateUser = async (selectedUser) => {
    const userData = {
      id: selectedUser.id,
      details: {
        name: `${firstName} ${lastName}`,
        role: Number(role),
        back_office_staff_id: selectedUser.staffId,
      },
      phone: phoneNumber || null,
      email: email || null,
    };
    const payload = {
      activity: `User ${firstName} ${lastName} has been updated by User - ${currentUser?.details?.name || ""
        }`,
      type: ACTIVITY_TYPE.USER_UPDATE,
      data: userData,
    };
    try {
      setIsSubmitting(true);

      await updateUserAction(userData).unwrap();

      const usersList = users.map((user) => {
        return user.id === selectedUser.id
          ? {
            ...user,
            firstname: firstName,
            lastname: lastName,
            email: email,
            phone: phoneNumber,
            // roles: [...(user?.roles || []), role],
            roles: [organisationRoles.find(r => r.id === Number(role))?.name],
          }
          : user;
      });

      setUsers(usersList);
      toast.success("User updated successfully");
      setIsModalOpen(false);
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.SUCCESS }).unwrap();
      return true;
    } catch (error) {
      toast.error(error.data?.message || "Failed to update user");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.FAILED }).unwrap();
      return false;
    }
  };

  const fetchOrganisationRoles = async () => {
    try {
      const { data } = await fetchOrganisationRolesAction({ orgId: currentUser.organisation.id }).unwrap();
      setOrganisationRoles(data);
    } catch (error) {
      console.error(error.data.message);
    }
  };
  useEffect(() => {
    fetchOrganisationRoles();
  }, []);

  const formatPhoneNumber = (phoneNumberString) => {
    const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    return null;
  };

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);

    setFirstName("");
    setLastName("");
    setEmail("");
    setPhoneNumber("");
    setRole("");
    setEditingUser(null);
    setIsEditing(false);
  };

  const matchesSearch = (user) => {
    const search = searchQuery.toLowerCase();
    return (
      `${user.firstname} ${user.lastname}`.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.phone?.toLowerCase().includes(search)
    );
  };

  const matchesStatus = (user) => {
    if (statusFilter.length === 0) return true;
    return statusFilter.includes(UserStatus[user.status]);
  };

  const handleUserSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const getSelectedUserRecords = () =>
    users.filter((user) => selectedUsers.includes(user.id));

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(
        users
          .filter(matchesSearch)
          .filter(matchesStatus)
          .filter((user) => user.id !== currentUser.id)
          .map((user) => user.id)
      );
    }
    setSelectAll(!selectAll);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "Admin":
        return "bg-yellow-100 text-yellow-800";
      case "Scheduler":
        return "bg-green-100 text-green-800";
      case "Executive":
        return "bg-blue-100 text-blue-800";
      default:
        // return "bg-gray-100 text-gray-800";
        return "bg-yellow-100 text-yellow-800"
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Invite Sent":
        return "bg-yellow-100 text-yellow-800";
      case "Inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleEdit = (user) => {
    setIsEditing(true);
    setEditingUser(user);
    setFirstName(user.firstname);
    setLastName(user.lastname);
    setEmail(user.email);
    setPhoneNumber(user.phone || "");
    // Find the role ID that matches the first role name from user.roles array
    const userRoleName = user.roles?.[0];
    const matchingRole = organisationRoles.find(r => r.name === userRoleName);
    setRole(matchingRole ? String(matchingRole.id) : "");
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let success = false;
    if (isEditing) {
      success = await updateUser(editingUser);
    } else {
      success = await createUser();
    }

    if (success) {
      setIsModalOpen(false);
      setIsEditing(false);
      setEditingUser(null);
      // Reset form
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhoneNumber("");
      setRole("");
    }
  };

  const handleConfirmAction = async () => {
    if (confirmAction === 'deactivate') {
      await handleDeactivateUsers();
    } else if (confirmAction === 'sendInvites') {
      await handleSendInvites();
    }
    setIsConfirmModalOpen(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleDeactivateClick = () => {
    setConfirmAction('deactivate');
    setIsConfirmModalOpen(true);
  };

  const handleSendInvitesClick = () => {
    setConfirmAction('sendInvites');
    setIsConfirmModalOpen(true);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeactivateUsers = async () => {
    const selectedUserRecords = getSelectedUserRecords();
    const payload = {
      activity: `Users ${selectedUserRecords.map((user) => user.firstname + " " + user.lastname).join(", ")} have been deactivated by User - ${currentUser?.details?.name || ""
        }`,
      type: ACTIVITY_TYPE.USER_DELETE,
      data: {
        userIds: selectedUsers,
      },
    };
    try {
      setIsSubmitting(true);
      await deleteManyBackOfficeUserAction({
        userIds: selectedUsers,
      }).unwrap();

      const updatedUsers = users.map((user) => {
        if (selectedUsers.includes(user.id)) {
          return { ...user, status: "REMOVED" };
        }
        return user;
      });

      setUsers(updatedUsers);
      setSelectedUsers([]);
      setSelectAll(false);
      toast.success("Users deactivated successfully");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.SUCCESS }).unwrap();
    } catch (error) {
      toast.error(error.data?.message || "Failed to deactivate users");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.FAILED }).unwrap();
    }
  };

  const handleSendInvites = async () => {
    const selectedUserRecords = getSelectedUserRecords();
    const payload = {
      activity: `Invites have been sent to users ${selectedUserRecords.map((user) => user.firstname + " " + user.lastname).join(", ")} by User - ${currentUser?.details?.name || ""
        }`,
      type: ACTIVITY_TYPE.INVITE_USER,
      data: {
        userIds: selectedUsers,
      },
    };
    try {
      setIsSubmitting(true);
      await inviteManyBackOfficeUserAction({
        userIds: selectedUsers,
      }).unwrap();

      const updatedUsers = users.map((user) => {
        if (selectedUsers.includes(user.id)) {
          return { ...user, status: "INVITED" };
        }
        return user;
      });

      setUsers(updatedUsers);
      setSelectedUsers([]);
      setSelectAll(false);
      toast.success("Invites sent successfully");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.SUCCESS }).unwrap();
    } catch (error) {
      toast.error(error.data?.message || "Failed to send invites");
      setIsSubmitting(false);
      await createLog({ ...payload, status: ACTIVITY_STATUS.FAILED }).unwrap();
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleStatusFilter = (value) => {
    if (statusFilter.indexOf(value) === -1) {
      setStatusFilter([...statusFilter, value]);
    } else {
      setStatusFilter(statusFilter.filter((filter) => filter !== value));
    }
  };

  const visibleUsers = users
    .filter(matchesSearch)
    .filter(matchesStatus)
    .sort((a, b) =>
      `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`)
    );

  return (
    <div className="min-h-[calc(100vh-70px)] bg-gray-50 w-full">
      {loading && <Loading loading={loading} />}
      {/* Main Content */}
      <div className="px-8 py-8 mx-auto w-[calc(100vw-280px)] sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="flex items-center gap-3 text-3xl font-bold text-gray-900">
            Users
            <span className="px-3 py-1 text-base font-medium text-gray-600 bg-gray-200 rounded-full">
              {visibleUsers.length}
              {(searchQuery || statusFilter.length > 0) &&
                visibleUsers.length !== users.length &&
                ` / ${users.length}`}
            </span>
          </h1>
          <div className="flex items-center gap-4">
            <FilterSelect
              variant="light"
              value={statusFilter}
              handleChange={handleStatusFilter}
              data={["Active", "Inactive", "Invite Sent"]}
              placeholder="Filter by Status..."
            />
            <div className="relative w-1/3">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <circle
                    cx="9"
                    cy="9"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M14 14l3 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full py-2 pl-10 pr-3 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              onClick={toggleModal}
              className="flex items-center gap-2 px-6 py-2 font-semibold text-white transition-colors bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 hover:shadow-md w-1/3"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 5v10M5 10h10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Add User
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 mb-6 text-white bg-blue-600 rounded-lg">
            <div className="flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-5 h-5 rounded cursor-pointer"
              />
              <span>
                {selectedUsers.length} user{selectedUsers.length > 1 ? "s" : ""}{" "}
                selected
              </span>
            </div>
            <div className="flex gap-3">
              {/* <button
                onClick={handleSendInvitesClick}
                disabled={isSubmitting}
                className="px-4 py-2 text-white transition-colors bg-blue-500 border border-blue-300 rounded-md bg-opacity-20 border-opacity-30 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Adding..." : "Add User"}
              </button> */}
              <button
                onClick={handleDeactivateClick}
                disabled={isSubmitting}
                className="px-4 py-2 text-white transition-colors bg-blue-500 border border-blue-300 rounded-md bg-opacity-20 border-opacity-30 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Deactivating..." : "Deactivate"}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-hidden bg-white shadow-sm rounded-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="w-10 px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded cursor-pointer"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Contact
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleUsers.length === 0 && !isGetLoading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-sm text-center text-gray-500"
                    >
                      No users match your search or filters.
                    </td>
                  </tr>
                )}
                {visibleUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        disabled={user.id === currentUser.id}
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserSelect(user.id)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 font-medium text-gray-600 bg-gray-200 rounded-full">
                          {user.initials}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstname} {user.lastname}
                            {user.id === currentUser.id && " (You)"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(
                          user.roles?.[0]
                        )}`}
                      >
                        {user.roles?.join(", ")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        <div className="flex items-center gap-1.5 mb-1">
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M2 5.5C2 4.67157 2.67157 4 3.5 4H12.5C13.3284 4 14 4.67157 14 5.5V10.5C14 11.3284 13.3284 12 12.5 12H3.5C2.67157 12 2 11.3284 2 10.5V5.5Z"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                            <path
                              d="M2 6L8 9L14 6"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                          </svg>
                          {user.email}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <rect
                              x="4"
                              y="2"
                              width="8"
                              height="12"
                              rx="2"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                            <circle
                              cx="8"
                              cy="11"
                              r="0.5"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                          </svg>
                          {user.phone ? (
                            formatPhoneNumber(user.phone)
                          ) : (
                            <span className="text-gray-400">
                              Not provided
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                          UserStatus[user.status]
                        )}`}
                      >
                        <span className="w-2 h-2 rounded-full mr-1.5 bg-current"></span>
                        {UserStatus[user.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg
                            className="h-3.5 w-3.5 mr-1.5"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M11.333 2A1.885 1.885 0 0114 4.667l-9 9-3.667 1 1-3.667 9-9z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Edit
                        </button>
                        {user.id !== currentUser.id && (
                          <button
                            onClick={() => handleDeleteClick(user)}
                            className="inline-flex items-center px-2 py-1.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-red-50 hover:text-red-700 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            {/* <svg
                                className="h-3.5 w-3.5"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg> */}
                            {user.status === 'REMOVED' ? "Reactivate" : "Deactivate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-xl">
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-2xl font-bold text-gray-900">{userToDelete.status === 'REMOVED' ? "Reactivate User" : "Deactivate User"}</h2>
              <p className="mt-2 text-gray-600">
                {userToDelete.status === 'REMOVED' ? `Are you sure you want to reactivate ${userToDelete.firstname} ${userToDelete.lastname}?` : `Are you sure you want to deactivate ${userToDelete.firstname} ${userToDelete.lastname}?`}
              </p>
            </div>
            <div className="px-6 py-4">
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setUserToDelete(null);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => deleteUser(userToDelete)}
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${userToDelete.status === 'REMOVED' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? userToDelete.status === 'REMOVED' ? "Reactivating..." : "Deactivating..." : userToDelete.status === 'REMOVED' ? "Reactivate" : "Deactivate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h2 className="text-2xl font-bold text-gray-900">{isEditing ? "Edit User" : "Add User"}</h2>
              <button
                onClick={toggleModal}
                className="p-2 text-gray-500 bg-gray-100 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter first name"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter last name"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="user@example.com"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    An invitation will be sent to this email address
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(formatPhoneNumber(e.target.value));
                    }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                  <p className="mt-1 text-sm text-gray-500">Optional</p>
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={role}
                    disabled={isEditing && currentUser.id === editingUser?.id}
                    onChange={(e) => setRole(e.target.value)}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm appearance-none bg-white bg-no-repeat bg-[length:20px] bg-[center_right_0.5rem] focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                    }}
                    required
                  >
                    <option value="">Select a role</option>
                    {/* <optgroup label="Administrative">
                      <option value="Admin">Admin</option>
                      <option value="Scheduler">Scheduler</option>
                      {isEditing &&
                        currentUser.id === editingUser?.id &&
                        editingUser.role === 4 && (
                          <option value="Creator">Creator</option>
                        )}
                    </optgroup> */}
                    {organisationRoles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Determines user permissions and access levels
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setIsEditing(false);
                      setEditingUser(null);
                      setFirstName("");
                      setLastName("");
                      setEmail("");
                      setPhoneNumber("");
                      setRole("");
                    }}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting
                      ? isEditing
                        ? "Updating..."
                        : "Sending..."
                      : isEditing
                        ? "Update User"
                        : "Add User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-xl">
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {confirmAction === 'deactivate' && 'Deactivate Users'}
                {confirmAction === 'sendInvites' && 'Add User'}
              </h2>
              <p className="mt-2 text-gray-600">
                {confirmAction === 'deactivate' && `Are you sure you want to deactivate ${selectedUsers.length} selected user${selectedUsers.length > 1 ? 's' : ''}?`}
                {confirmAction === 'sendInvites' && `Are you sure you want to add ${selectedUsers.length} selected user${selectedUsers.length > 1 ? 's' : ''}?`}
              </p>
            </div>
            <div className="px-6 py-4">
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsConfirmModalOpen(false);
                    setConfirmAction(null);
                    setConfirmData(null);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAction}
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmAction === 'deactivate'
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting
                    ? confirmAction === 'deactivate'
                      ? 'Deactivating...'
                      : 'Adding...'
                    : confirmAction === 'deactivate'
                      ? 'Deactivate'
                      : 'Add User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
